import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Bot, Zap, Loader2, Radar, Power, Save, Bell, BellOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const LOG_COLORS = { BUY: "text-[#CCFF00]", SELL: "text-[#FF3366]", SCAN: "text-white/50", INFO: "text-[#00CCFF]", RECAP: "text-[#FFB800]" };

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export default function AutoTrade() {
  const { t } = useLang();
  const [settings, setSettings] = useState(null);
  const [chains, setChains] = useState([]);
  const [activity, setActivity] = useState([]);
  const [positions, setPositions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((s) => setPushOn(!!s))
    ).catch(() => {});
  }, []);

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error(t("push_unsupported"));
      return;
    }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error(t("push_denied")); return; }
      const { data } = await client.get("/push/vapid-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.key),
      });
      const json = sub.toJSON();
      await client.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setPushOn(true);
      toast.success(t("push_enabled_msg"));
    } catch (e) {
      toast.error(t("push_failed"));
    } finally { setPushBusy(false); }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await client.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setPushOn(false);
      toast.success(t("push_disabled_msg"));
    } catch (e) { /* noop */ } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    try {
      const { data } = await client.post("/push/test");
      toast.success(`${t("push_test_sent")} (${data.sent})`);
    } catch (e) { toast.error("Error"); }
  };

  const load = useCallback(async () => {
    try {
      const [s, c, a, p] = await Promise.all([
        client.get("/autotrade/settings"),
        client.get("/market/chains"),
        client.get("/autotrade/activity?limit=40"),
        client.get("/portfolio/positions"),
      ]);
      setSettings(s.data);
      setChains(c.data || []);
      setActivity(a.data || []);
      setPositions((p.data || []).filter((x) => x.auto));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, [load]);

  const save = async (patch, silent = false) => {
    setSaving(true);
    try {
      const { data } = await client.post("/autotrade/settings", { ...settings, ...patch });
      setSettings(data);
      if (!silent) toast.success(t("settings_saved"));
      client.get("/autotrade/activity?limit=40").then((r) => setActivity(r.data || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const toggle = async (enabled) => {
    await save({ enabled }, true);
    toast.success(enabled ? t("bot_on_msg") : t("bot_off_msg"));
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await client.post("/autotrade/run-now");
      toast.success(`${t("scan_done")} — ${data.tokens_scanned} tokens`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enable the bot first");
    } finally { setRunning(false); }
  };

  const toggleChain = (c) => {
    let next = settings.chains.includes("all") ? [] : [...settings.chains];
    if (next.includes(c)) next = next.filter((x) => x !== c);
    else next.push(c);
    if (next.length === 0) next = ["all"];
    setSettings({ ...settings, chains: next });
  };

  if (!settings) return <div className="p-20 text-center"><Loader2 className="inline animate-spin text-[#CCFF00]" /></div>;

  const allChains = settings.chains.includes("all");

  return (
    <div data-testid="autotrade-page" className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">Auto-Pilot</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-3">
            <Bot size={32} className="text-[#CCFF00]" /> {t("autotrade_title")}
          </h1>
          <p className="text-white/50 text-sm mt-2 max-w-xl">{t("autotrade_sub")}</p>
        </div>
        <div className="flex items-center gap-4">
          <button data-testid="autotrade-run-now" onClick={runNow} disabled={running || !settings.enabled}
            className="btn-ghost !py-2.5 !px-4 !text-xs flex items-center gap-2 disabled:opacity-40">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />} {t("run_now")}
          </button>
          <div className={`flex items-center gap-3 px-5 py-3 border ${settings.enabled ? "border-[#CCFF00] bg-[#CCFF00]/5" : "border-[#222]"}`}>
            <Power size={16} className={settings.enabled ? "text-[#CCFF00]" : "text-white/40"} />
            <span className={`font-mono text-xs uppercase font-bold ${settings.enabled ? "text-[#CCFF00]" : "text-white/40"}`}>
              {settings.enabled ? t("bot_active") : t("bot_inactive")}
            </span>
            <Switch data-testid="autotrade-master-toggle" checked={settings.enabled} onCheckedChange={toggle} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-px bg-[#222] border border-[#222]">
        {/* Settings */}
        <div className="col-span-12 lg:col-span-5 bg-[#0A0A0A] p-6">
          <div className="font-display text-sm uppercase tracking-widest mb-6 flex items-center gap-2">
            <Zap size={14} className="text-[#CCFF00]" /> {t("bot_settings")}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t("amount_per_trade")}>
              <input data-testid="autotrade-amount-input" type="number" min="10" value={settings.amount_per_trade_usd}
                onChange={(e) => setSettings({ ...settings, amount_per_trade_usd: Number(e.target.value) })}
                className="input-dark" />
            </Field>
            <Field label={t("max_positions")}>
              <input data-testid="autotrade-maxpos-input" type="number" min="1" max="50" value={settings.max_positions}
                onChange={(e) => setSettings({ ...settings, max_positions: Number(e.target.value) })}
                className="input-dark" />
            </Field>
            <Field label={`${t("min_confidence")} (%)`}>
              <input data-testid="autotrade-conf-input" type="number" min="50" max="99" value={Math.round(settings.min_confidence * 100)}
                onChange={(e) => setSettings({ ...settings, min_confidence: Number(e.target.value) / 100 })}
                className="input-dark" />
            </Field>
          </div>

          <div className="mt-6 border border-[#222] p-4 flex items-center justify-between">
            <div>
              <div className="font-mono text-xs font-bold text-[#CCFF00]">{t("ai_dynamic")}</div>
              <div className="text-[11px] text-white/40 mt-1">{t("ai_dynamic_desc")}</div>
            </div>
            <Switch data-testid="autotrade-aidynamic-toggle" checked={settings.ai_dynamic}
              onCheckedChange={(v) => setSettings({ ...settings, ai_dynamic: v })} />
          </div>

          {!settings.ai_dynamic && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field label={`${t("take_profit")} (%)`}>
                <input data-testid="autotrade-tp-input" type="number" min="5" value={settings.take_profit_pct}
                  onChange={(e) => setSettings({ ...settings, take_profit_pct: Number(e.target.value) })}
                  className="input-dark" />
              </Field>
              <Field label={`${t("stop_loss")} (%)`}>
                <input data-testid="autotrade-sl-input" type="number" min="5" max="95" value={settings.stop_loss_pct}
                  onChange={(e) => setSettings({ ...settings, stop_loss_pct: Number(e.target.value) })}
                  className="input-dark" />
              </Field>
            </div>
          )}

          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mb-3">{t("chains_title")}</div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="chain-chip-all" onClick={() => setSettings({ ...settings, chains: ["all"] })}
                className={`px-3 py-1.5 text-[10px] font-mono uppercase border ${allChains ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#222] text-white/60 hover:border-[#CCFF00]"}`}>
                {t("all_chains")}
              </button>
              {chains.map((c) => (
                <button key={c} data-testid={`chain-chip-${c}`} onClick={() => toggleChain(c)}
                  className={`px-3 py-1.5 text-[10px] font-mono uppercase border ${!allChains && settings.chains.includes(c) ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#222] text-white/60 hover:border-[#CCFF00]"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <button data-testid="autotrade-save-button" onClick={() => save({})} disabled={saving}
            className="btn-acid !py-3 !px-6 !text-xs mt-8 w-full flex items-center justify-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t("save_settings")}
          </button>

          {/* Push notifications */}
          <div className="mt-6 border border-[#222] p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {pushOn ? <Bell size={16} className="text-[#CCFF00]" /> : <BellOff size={16} className="text-white/40" />}
                <div>
                  <div className="font-mono text-xs font-bold">{t("push_title")}</div>
                  <div className="text-[11px] text-white/40 mt-0.5">{t("push_desc")}</div>
                </div>
              </div>
              <Switch data-testid="push-toggle" checked={pushOn} disabled={pushBusy}
                onCheckedChange={(v) => (v ? enablePush() : disablePush())} />
            </div>
            {pushOn && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button data-testid="push-test-button" onClick={testPush}
                  className="btn-ghost !py-2 !px-3 !text-[10px]">{t("push_test")}</button>
                <button data-testid="recap-now-button" onClick={async () => {
                  try { await client.post("/autotrade/recap-now"); toast.success(t("recap_sent")); load(); } catch (e) { toast.error("Error"); }
                }} className="btn-ghost !py-2 !px-3 !text-[10px]">{t("recap_now")}</button>
              </div>
            )}
          </div>
        </div>

        {/* Activity feed */}
        <div className="col-span-12 lg:col-span-7 bg-[#0A0A0A]">
          <div className="px-6 py-4 border-b border-[#222] font-display text-sm uppercase tracking-widest flex items-center justify-between">
            <span>{t("activity_feed")}</span>
            <span className="text-[10px] font-mono text-white/40 normal-case">{t("scan_freq")}</span>
          </div>
          <div className="max-h-[560px] overflow-y-auto" data-testid="autotrade-activity-feed">
            {activity.length === 0 && (
              <div className="p-10 text-center text-white/40 text-sm font-mono">{t("no_activity")}</div>
            )}
            {activity.map((a) => (
              <div key={a.id} className="px-6 py-3 border-b border-[#222] last:border-0 flex items-start gap-3">
                <span className={`text-[9px] font-mono font-bold mt-0.5 w-10 shrink-0 ${LOG_COLORS[a.type] || "text-white/50"}`}>{a.type}</span>
                <div className="flex-1">
                  <div className="text-[11px] text-white/80 leading-relaxed font-mono">{a.message}</div>
                  <div className="text-[9px] text-white/30 font-mono mt-1">{new Date(a.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Auto positions */}
      <div className="mt-10 surface">
        <div className="px-6 py-4 border-b border-[#222] font-display text-sm uppercase tracking-widest flex items-center gap-2">
          <Bot size={14} className="text-[#CCFF00]" /> {t("auto_positions")}
        </div>
        {positions.length === 0 && <div className="p-8 text-center text-white/40 text-sm font-mono">{t("no_auto_positions")}</div>}
        {positions.map((p) => (
          <div key={p.id} className="px-6 py-4 border-b border-[#222] last:border-0 grid grid-cols-12 items-center gap-2" data-testid={`auto-pos-${p.id}`}>
            <div className="col-span-2 font-display font-semibold">{p.token_symbol}</div>
            <div className="col-span-1"><span className="text-[9px] border border-[#222] text-white/60 px-1.5 py-0.5 font-mono uppercase">{p.chain}</span></div>
            <div className="col-span-1 font-mono text-xs">${p.amount_usd}</div>
            <div className="col-span-2 font-mono text-[10px] text-white/50">TP +{Number(p.take_profit_pct || 0).toFixed(0)}% / SL -{Number(p.stop_loss_pct || 0).toFixed(0)}%</div>
            <div className={`col-span-2 font-mono text-xs ${p.pnl_pct >= 0 ? "text-[#CCFF00]" : "text-[#FF3366]"}`}>
              {p.pnl_pct >= 0 ? "+" : ""}{Number(p.pnl_pct).toFixed(2)}%
            </div>
            <div className="col-span-2 font-mono text-[10px] text-white/40">
              conf {p.ai_confidence ? `${Math.round(p.ai_confidence * 100)}%` : "—"} · {p.risk_level || ""}
            </div>
            <div className="col-span-2 text-right font-mono text-[10px]">
              {p.status === "open"
                ? <span className="text-[#CCFF00]">OPEN</span>
                : <span className="text-white/40">{(p.close_reason || "closed").toUpperCase()}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mb-2">{label}</div>
      {children}
    </div>
  );
}
